/**
 * 国际化 · 入口
 * ------------------------------------------------------------
 * 壳层文案统一从此取。useI18n() 返回 computed（模板自动解包，
 * JS 上下文取 .value），模板里直接 `{{ t.nav.chat }}` 用。
 *
 * 语言与地区：支持多语言多地区清单（LOCALES）；当前翻译包覆盖
 * 简中/英文，其余语言按回退链回落（繁中→简中，其他→英文），
 * 翻译包随 M2 国际化实装逐步补齐。
 * 当前语言来源：localStorage ema.lang.v1（存完整 locale id，
 * 兼容旧值 zh/en），未设则取浏览器 navigator.language。
 */
import { computed, reactive } from 'vue'
import { zhCN } from './zh-CN'
import { enUS } from './en-US'
import type { Dict } from './zh-CN'
import { readParam } from '@/shared/query'

export type LangCode = string

export interface LocaleDef { id: string; native: string; region: string; ready: boolean }

/** 支持的语言与地区（ready=翻译包已实装；未实装可选、界面回退） */
export const LOCALES: LocaleDef[] = [
  { id: 'zh-CN', native: '简体中文', region: '中国', ready: true },
  { id: 'zh-TW', native: '繁體中文', region: '台灣', ready: false },
  { id: 'zh-HK', native: '繁體中文', region: '香港', ready: false },
  { id: 'en-US', native: 'English (US)', region: 'United States', ready: true },
  { id: 'en-GB', native: 'English (UK)', region: 'United Kingdom', ready: false },
  { id: 'ja-JP', native: '日本語', region: '日本', ready: false },
  { id: 'ko-KR', native: '한국어', region: '대한민국', ready: false },
  { id: 'fr-FR', native: 'Français', region: 'France', ready: false },
  { id: 'de-DE', native: 'Deutsch', region: 'Deutschland', ready: false },
  { id: 'es-ES', native: 'Español', region: 'España', ready: false },
  { id: 'ru-RU', native: 'Русский', region: 'Россия', ready: false }
]

const DICTS: Record<'zh' | 'en', Dict> = { zh: zhCN, en: enUS }
const LS_KEY = 'ema.lang.v1'

/** locale → 字典回退链：繁中→简中，其余→英文 */
function baseDict(id: string): 'zh' | 'en' {
  return id.startsWith('zh') ? 'zh' : 'en'
}

function normalize(id: string | null): string | null {
  if (!id) return null
  if (id === 'zh') return 'zh-CN'
  if (id === 'en') return 'en-US'
  return LOCALES.some(l => l.id === id) ? id : null
}

function detect(): string {
  /* URL 覆盖优先（演示链接 / 无头验收），但不落库 */
  const ov = normalize(readParam('lang'))
  if (ov) return ov
  const saved = normalize(localStorage.getItem(LS_KEY))
  if (saved) return saved
  const nav = normalize(navigator.language ?? null)
  return nav ?? (navigator.language?.toLowerCase().startsWith('en') ? 'en-US' : 'zh-CN')
}

const state = reactive<{ lang: string; t: Dict }>({
  lang: detect(),
  t: DICTS[baseDict(detect())]
})

export function useI18n() {
  const lang = computed(() => state.lang)
  const t = computed(() => state.t)
  function setLang(id: string) {
    const known = LOCALES.some(l => l.id === id)
    state.lang = known ? id : 'zh-CN'
    state.t = DICTS[baseDict(state.lang)]
    localStorage.setItem(LS_KEY, state.lang)
    document.documentElement.lang = state.lang
  }
  return { lang, t, setLang }
}
